import { customInstance } from "@repo/api-client";
import type { WindowDraft } from "./scheduleRules";

/**
 * Client contract for the scheduling REST surface (`/api/v1/venues/{venueId}/...`, §C.4). Goes through
 * `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), NOT a raw fetch — a 409 stale
 * write and a 422 rejection come back as `ApiError` carrying the RFC 9457 problem. The Orval client is
 * regenerated from the backend OpenAPI at serial integration (`just gen`); these thin accessors are
 * the M1 stand-in, matching the shell's `staffApi` pattern.
 */

export interface ServiceResponse {
  id: string;
  venueId: string;
  nameFr: string;
  name: Record<string, string>;
  partyMin: number;
  partyMax: number;
  onlineBookable: boolean;
  bookingMode: string;
  confirmationMode: string;
  requireCreditCard: string;
  turnoverBufferMinutes: number;
  turnTimeDefaultMinutes: number | null;
  slotGranularityMinutes: number | null;
  leadTimeMinutes: number | null;
  horizonDays: number | null;
  requestHoldWindowMinutes: number;
  coversCapPerSlot: number | null;
  version: number;
  defaultsApplied: {
    turnTime: boolean;
    slotGranularity: boolean;
    leadTime: boolean;
    horizon: boolean;
  };
}

export interface ServiceRequest {
  nameFr: string;
  nameEn?: string;
  partyMin: number;
  partyMax: number;
  onlineBookable: boolean;
  turnoverBufferMinutes: number;
  slotGranularityMinutes?: number | null;
  coversCapPerSlot?: number | null;
  version?: number;
}

export interface WindowResponse {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  crossesMidnight: boolean;
  serviceId: string;
}

export interface ScheduleResponse {
  version: number;
  windows: WindowResponse[];
}

/** The venue-wide no-show guarantee mode. "DEPOSIT" is not yet a value (capture unbuilt) — the UI offers it disabled. */
export type GuaranteeMode = "OFF" | "NO_SHOW_FEE";

export interface BookingPolicyResponse {
  onlineBookingEnabled: boolean;
  cancellationCutoffMinutes: number | null;
  gracePeriodMinutes: number;
  noShowPolicyText: string | null;
  reminderOffsetMinutes: number | null;
  /** Venue-wide no-show guarantee: NO_SHOW_FEE makes every online booking require a card. */
  guaranteeMode: GuaranteeMode;
  /** The no-show fee in integer minor units (ADR-0024); non-null only when guaranteeMode is NO_SHOW_FEE. */
  noShowFeeAmount: number | null;
  noShowFeeCurrency: string | null;
  version: number;
  /** Read-only: someone at the venue holds a usable manager PIN, so a no-show can actually be charged. */
  managerPinConfigured?: boolean;
}

export interface CalendarExceptionResponse {
  date: string;
  kind: "CLOSED" | "OVERRIDE";
  windows: Array<{
    startTime: string;
    endTime: string;
    crossesMidnight: boolean;
    serviceId: string;
  }>;
  reason: string | null;
  version: number;
}

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}`;

export const scheduleApi = {
  listServices: (venueId: string) =>
    customInstance<ServiceResponse[]>(`${base(venueId)}/services`, { method: "GET" }),

  createService: (venueId: string, body: ServiceRequest) =>
    customInstance<ServiceResponse>(`${base(venueId)}/services`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  updateService: (venueId: string, serviceId: string, body: ServiceRequest) =>
    customInstance<ServiceResponse>(`${base(venueId)}/services/${encodeURIComponent(serviceId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  getSchedule: (venueId: string) =>
    customInstance<ScheduleResponse>(`${base(venueId)}/schedule`, { method: "GET" }),

  saveSchedule: (venueId: string, version: number, windows: WindowDraft[]) =>
    customInstance<ScheduleResponse>(`${base(venueId)}/schedule`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version, windows }),
    }),

  getPolicy: (venueId: string) =>
    customInstance<BookingPolicyResponse>(`${base(venueId)}/booking-policy`, { method: "GET" }),

  savePolicy: (
    venueId: string,
    body: Omit<BookingPolicyResponse, "cancellationCutoffMinutes"> & {
      cancellationCutoffMinutes: number | null;
    },
  ) => {
    // Forms spread the loaded policy into their body; this one is computed by the server, not saved.
    const { managerPinConfigured: _readOnly, noShowFeeCurrency, ...writable } = body;
    return customInstance<BookingPolicyResponse>(`${base(venueId)}/booking-policy`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      // The response's currency is null when no fee is set, but the request's is a non-null string
      // with a server default: sending the null back is a 400, so it is left out instead.
      body: JSON.stringify(
        noShowFeeCurrency === null ? writable : { ...writable, noShowFeeCurrency },
      ),
    });
  },

  listExceptions: (venueId: string) =>
    customInstance<CalendarExceptionResponse[]>(`${base(venueId)}/calendar-exceptions`, {
      method: "GET",
    }),

  setException: (
    venueId: string,
    date: string,
    body: {
      kind: "CLOSED" | "OVERRIDE";
      windows?: WindowDraft[];
      reason?: string | null;
      version: number;
    },
  ) =>
    customInstance<CalendarExceptionResponse>(
      `${base(venueId)}/calendar-exceptions/${encodeURIComponent(date)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    ),

  deleteException: (venueId: string, date: string) =>
    customInstance<void>(`${base(venueId)}/calendar-exceptions/${encodeURIComponent(date)}`, {
      method: "DELETE",
    }),
};

/** The `["venue", venueId, "services"]` query-key family this feature owns (eventQueryKeyMap convention). */
export const scheduleKeys = {
  services: (venueId: string) => ["venue", venueId, "services"] as const,
  schedule: (venueId: string) => ["venue", venueId, "services", "schedule"] as const,
  policy: (venueId: string) => ["venue", venueId, "services", "policy"] as const,
  exceptions: (venueId: string) => ["venue", venueId, "services", "exceptions"] as const,
};

/** The `rejections` array the whole-week PUT returns in its 422 problem (SCHEDULE_REJECTED). */
export interface ScheduleRejection {
  type: "OVERLAPPING_WINDOWS" | "END_BEFORE_START_WITHOUT_CROSS_MIDNIGHT";
  windows: number[];
}

/** Extract the offending window indices from a SCHEDULE_REJECTED 422 problem body. */
export function rejectedWindowIndices(problem: unknown): Set<number> {
  const out = new Set<number>();
  const rejections = (problem as { rejections?: ScheduleRejection[] } | undefined)?.rejections;
  for (const r of rejections ?? []) for (const i of r.windows) out.add(i);
  return out;
}
